import type { AxiosInstance } from "axios";
import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  CalendarDays,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { FormulaList, HighlightCard, HighlightGrid, noteText } from "../components/ReportGuide/GuideContent.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { Card } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Select } from "../components/ui/select.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { TableResizeHandle, useTableResize } from "../components/TableResizeHandle.js";
import { daysBefore, toLocalDate } from "../lib/dates.js";

/**
 * Daily Performance — "Campaign & Ad Pulse" (`model="fc"`) and "Ad Pulse" (`model="lc"`).
 *
 * Ported from `ecom_code`'s DailyPerformance / DailyAdPerformance. The rule (owner-
 * confirmed 2026-09-11): a "hidden winner" is BELOW the account platform-ROAS threshold
 * but AT OR ABOVE the account model-ROAS threshold, both = account ROAS × 0.90. Spend
 * floor ₹2,000; a data gap (no model revenue) is NOT a winner.
 *
 * Layout follows legacy — the benchmark-rule strip, the "don't kill these" callout, and
 * BOTH the rolling-4-day and yesterday blocks stacked on one page. The table, type scale
 * and colour tokens are the platform's (`ReportTable` / `styles.css`).
 *
 * "Ad Pulse" is ad-grain only (owner, 2026-09-11). "Campaign & Ad Pulse" can switch
 * between campaigns and ads.
 */

const RATIO = 0.9;

interface PulseItem {
  readonly entity_id: string;
  readonly entity_name: string;
  readonly spend: number;
  readonly platform_revenue: number;
  readonly platform_roas: number;
  readonly model_revenue: number;
  readonly model_orders: number;
  readonly model_roas: number;
  readonly dda_revenue: number;
  readonly dda_roas: number;
  readonly is_qualified_winner: boolean;
}

interface PulseBenchmarks {
  readonly account_spend: number;
  readonly account_platform_revenue: number;
  readonly account_platform_roas: number;
  readonly platform_threshold: number;
  readonly account_model_revenue: number;
  readonly account_model_roas: number;
  readonly model_threshold: number;
}

interface PulseResponse {
  readonly platform: "meta" | "google";
  readonly entity: "campaign" | "ad";
  readonly model: "fc" | "lc";
  readonly timezone: string;
  readonly benchmarks: PulseBenchmarks;
  readonly total: number;
  readonly qualified: number;
  readonly rows: readonly PulseItem[];
}

type Platform = "meta" | "google";
type Entity = "campaign" | "ad";
type Tab = "qualified" | "all";
const PAGE_SIZE = 10;

const inr = (n: number): string => `₹${Math.round(n).toLocaleString("en-IN")}`;
const roas = (n: number): string => `${n.toFixed(2)}×`;

interface Period {
  readonly key: "rolling4" | "yesterday";
  readonly title: string;
  readonly pill: string;
  readonly from: string;
  readonly to: string;
  readonly rangeLabel: string;
}

function periods(today = new Date()): readonly Period[] {
  const yesterday = toLocalDate(daysBefore(today, 1)); // last CLOSED day
  const fourAgo = toLocalDate(daysBefore(today, 4));
  return [
    {
      key: "rolling4",
      title: "Last 4 days",
      pill: "Rolling window (4 closed days)",
      from: fourAgo,
      to: yesterday,
      rangeLabel: `${fourAgo} – ${yesterday}`,
    },
    {
      key: "yesterday",
      title: "Yesterday",
      pill: "Full-day analysis",
      from: yesterday,
      to: yesterday,
      rangeLabel: yesterday,
    },
  ];
}

export function DailyPulseScreen({
  client,
  tenantId,
  storeName,
  model,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  readonly storeName: string;
  readonly model: "fc" | "lc";
}): React.JSX.Element {
  // Ad Pulse (lc) is ad-grain only; Campaign & Ad Pulse (fc) can switch.
  const canSwitchEntity = model === "fc";
  /**
   * GOOGLE HIDDEN FOR NOW (owner request, 2026-09-15) — Meta only until Google's numbers
   * are ready to show. The platform plumbing below (this screen, the API query schema,
   * `dailyPulseQuery` in packages/ch) is built, tested (11/11 live-ClickHouse cases) and
   * UNCHANGED — only the toggle that lets a merchant pick "Google Ads" is disabled here.
   * Root cause already diagnosed and written up in `Provision.md` (2026-09-15): in a
   * short window, ~70% of Google's first-click revenue comes from campaigns with NO
   * spend rows in that exact window, which inflates the account benchmark past what any
   * real, currently-spending campaign can clear — so "0 qualified" is what shows today,
   * not a bug. To re-enable: uncomment the `<Select>` below and restore `setPlatform`.
   */
  const [platform] = useState<Platform>("meta");
  const [entity, setEntity] = useState<Entity>(model === "lc" ? "ad" : "campaign");
  const [search, setSearch] = useState("");
  const [showGuideSection, setShowGuideSection] = useState(false);
  const modelLabel = model === "lc" ? "LC" : "FC";
  const platformLabel = platform === "google" ? "Google" : "Meta";
  // The platform's own ads-manager product name — Meta's is "Meta Ads Manager", Google's
  // own product is just "Google Ads" (there is no separate "Manager" branding).
  const platformManagerLabel = platform === "google" ? "Google Ads" : "Meta Ads Manager";
  const period = useMemo(() => periods(), []);

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
            <BarChart3 className="text-primary size-6" />
            {model === "lc" ? "Ad Pulse" : "Campaign & Ad Pulse"}
            <ReportGuideToggleButton
              showGuide={showGuideSection}
              onToggle={() => setShowGuideSection((current) => !current)}
            />
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Google toggle disabled for now — see the note above `const [platform]`. */}
          {canSwitchEntity && (
            <Select
              value={entity}
              onChange={(e) => setEntity(e.target.value as Entity)}
              className="h-9 w-[10rem] text-xs font-medium"
              aria-label="View by"
            >
              <option value="campaign">View by: Campaigns</option>
              <option value="ad">View by: Ads</option>
            </Select>
          )}
          <div className="relative w-full sm:w-56">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
            <Input
              type="search"
              placeholder={`Filter ${entity === "ad" ? "ad" : "campaign"} name…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-8 text-xs"
              aria-label="Filter by name"
            />
          </div>
        </div>
      </header>

      {/*
        Guide content mirrors `ecom_code`'s `CampaignPulseReportNotes`/`CampaignPulseCalculationNotes`
        (fc) and `AdPulseReportNotes`/`AdPulseCalculationNotes` (lc) almost verbatim — same
        three-card structure and the same "Overall / Cutoff / Qualification" formula list.
        ADAPTED in one way legacy isn't: this report can also show Google (legacy is
        Meta-only, per its hard-coded "Meta Ads Manager" wording), so every mention of the
        platform reads off `platformManagerLabel` instead of being hard-coded to Meta.

        THIS IS NOW THE ONLY PLACE the 0.90× benchmark rule and the "don't cut these" case
        are explained in full — they used to ALSO sit as an always-visible strip and callout
        right below this accordion, repeating the same two sentences a third and fourth
        time on a page that already opens with a one-line summary above. Owner report,
        2026-09-15: it read as the same information stated four times. Collapsed by default
        here; the toggle button by the title expands it.
      */}
      <ReportGuideAccordion
        showGuide={showGuideSection}
        whatContent={
          <div className={noteText}>
            <p className="m-0">
              The <strong>{model === "lc" ? "Ad Pulse (LC Opportunity)" : "Campaign & Ad Pulse (FC Opportunity)"}</strong>{" "}
              report protects high-performing {model === "lc" ? "bottom-of-funnel customer conversion" : "top-of-funnel customer acquisition"}{" "}
              {entity === "ad" ? "ads" : "campaigns"} from being prematurely paused or cut:
            </p>
            <HighlightGrid>
              <HighlightCard tone="amber" title="1. Platform Underperformance Filter" icon={<AlertTriangle className="size-3.5" />}>
                Detects {entity === "ad" ? "ads" : "campaigns"} with {platformLabel} Platform ROAS &lt;{" "}
                <strong>{RATIO.toFixed(2)}× Account Platform ROAS</strong> that look like underperformers inside{" "}
                {platformManagerLabel}.
              </HighlightCard>
              {model === "lc" ? (
                <HighlightCard tone="purple" title="2. Last-Click (LC) Conversion Filter" icon={<Target className="size-3.5" />}>
                  Cross-verifies these same ads against verified last-touch Shopify attribution with LC ROAS ≥{" "}
                  <strong>{RATIO.toFixed(2)}× Account LC ROAS</strong>.
                </HighlightCard>
              ) : (
                <HighlightCard tone="emerald" title="2. First-Click (FC) Discovery Filter" icon={<ShieldCheck className="size-3.5" />}>
                  Cross-verifies these same {entity === "ad" ? "ads" : "campaigns"} against verified first-touch Shopify
                  attribution with FC ROAS ≥ <strong>{RATIO.toFixed(2)}× Account FC ROAS</strong>.
                </HighlightCard>
              )}
              <HighlightCard tone={model === "lc" ? "indigo" : "blue"} title="3. Media Buyer Action" icon={<Sparkles className="size-3.5" />}>
                <strong>Do Not Kill!</strong> These {entity === "ad" ? "ads" : "campaigns"} are{" "}
                {model === "lc" ? "essential closing engines driving the final purchase conversion" : "healthy acquisition engines feeding new customers into your sales funnel"}.
              </HighlightCard>
            </HighlightGrid>
            <p className="m-0">
              <strong>Business Logic classification:</strong> every {entity === "ad" ? "ad" : "campaign"} shown here is
              treated as {model === "lc" ? "a Conversion/Sales campaign" : "an Awareness campaign"}, regardless of the
              objective actually configured for it in {platformManagerLabel} — the classification follows the
              attribution model this report evaluates ({modelLabel === "LC" ? "Last-Click" : "First-Click"}), not the
              platform's own objective label.
            </p>
          </div>
        }
        howTitle={`How ${RATIO.toFixed(2)}× ${modelLabel} thresholds are calculated`}
        howContent={
          <div className={noteText}>
            <p className="m-0">Exact benchmark calculation rules used:</p>
            <FormulaList>
              <li>
                <strong>Overall {platformLabel} Platform ROAS:</strong> Total {platformLabel} Revenue ÷ Total{" "}
                {platformLabel} Spend across all {entity === "ad" ? "ads" : "campaigns"} for the period.
              </li>
              <li>
                <strong>Platform Cutoff Threshold:</strong> Overall Platform ROAS × {RATIO.toFixed(2)}. Any{" "}
                {entity === "ad" ? "ad" : "campaign"} with Platform ROAS below this is marked for review.
              </li>
              <li>
                <strong>Overall {platformLabel} {modelLabel} ROAS:</strong> Total {platformLabel}{" "}
                {model === "lc" ? "Last-Click" : "First-Click"} Revenue ÷ Total {platformLabel} Spend across the account.
              </li>
              <li>
                <strong>{modelLabel} Qualification Threshold:</strong> Overall {modelLabel} ROAS × {RATIO.toFixed(2)}.
              </li>
              <li>
                <strong>Qualification Rule:</strong> An entity qualifies as a{" "}
                <strong>{model === "lc" ? "Hidden Converter" : "Hidden TOF Winner"}</strong> if and only if Platform ROAS
                &lt; (Platform ROAS × {RATIO.toFixed(2)}) AND {modelLabel} ROAS ≥ ({modelLabel} ROAS × {RATIO.toFixed(2)}
                ) — with real {modelLabel} revenue; a data gap never qualifies.
              </li>
            </FormulaList>
          </div>
        }
      />

      {period.map((p) => (
        <PeriodBlock
          key={`${p.key}:${platform}:${entity}:${tenantId}`}
          client={client}
          storeName={storeName}
          platform={platform}
          platformLabel={platformLabel}
          platformManagerLabel={platformManagerLabel}
          model={model}
          modelLabel={modelLabel}
          entity={entity}
          period={p}
          search={search}
        />
      ))}
    </section>
  );
}

function PeriodBlock({
  client,
  storeName,
  platform,
  platformLabel,
  platformManagerLabel,
  model,
  modelLabel,
  entity,
  period,
  search,
}: {
  readonly client: AxiosInstance;
  readonly storeName: string;
  readonly platform: Platform;
  readonly platformLabel: string;
  readonly platformManagerLabel: string;
  readonly model: "fc" | "lc";
  readonly modelLabel: string;
  readonly entity: Entity;
  readonly period: Period;
  readonly search: string;
}): React.JSX.Element {
  const [data, setData] = useState<PulseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("qualified");
  const [page, setPage] = useState(1);
  const { colWidths, startResize } = useTableResize([420, 150, 150, 150, 150]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get<{ success: boolean; data: PulseResponse }>(
        "/api/v2/marketattribution/daily-pulse",
        { params: { from: period.from, to: period.to, platform, entity, model } },
      );
      setData(res.data.data);
    } catch (caught) {
      setError(apiErrorMessage(caught, `Could not load this period. Check that ${platformLabel} is connected for this store.`));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client, period.from, period.to, platform, platformLabel, entity, model]);

  useEffect(() => {
    void load();
    setTab("qualified");
    setPage(1);
  }, [load]);

  const bm = data?.benchmarks ?? null;
  const rows = data?.rows ?? [];
  const qualifiedRows = useMemo(() => rows.filter((r) => r.is_qualified_winner), [rows]);
  const protectedSpend = useMemo(() => qualifiedRows.reduce((s, r) => s + r.spend, 0), [qualifiedRows]);

  const filtered = useMemo(() => {
    const base = tab === "qualified" ? qualifiedRows : rows;
    const q = search.trim().toLowerCase();
    return q === "" ? base : base.filter((r) => r.entity_name.toLowerCase().includes(q));
  }, [tab, qualifiedRows, rows, search]);

  useEffect(() => {
    setPage(1);
  }, [tab, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const entityWord = entity === "ad" ? "ads" : "campaigns";

  const totals = useMemo(() => {
    const spend = filtered.reduce((s, r) => s + r.spend, 0);
    const prev = filtered.reduce((s, r) => s + r.platform_revenue, 0);
    const drev = filtered.reduce((s, r) => s + r.dda_revenue, 0);
    const mrev = filtered.reduce((s, r) => s + r.model_revenue, 0);
    return {
      spend,
      platform_roas: spend > 0 ? prev / spend : 0,
      dda_roas: spend > 0 ? drev / spend : 0,
      model_roas: spend > 0 ? mrev / spend : 0,
    };
  }, [filtered]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="text-primary bg-primary/10 flex size-7 shrink-0 items-center justify-center rounded-lg">
            {period.key === "yesterday" ? <CalendarDays className="size-4" /> : <CalendarClock className="size-4" />}
          </span>
          <div>
            <h2 className="text-foreground text-sm font-bold tracking-wide uppercase">{period.title}</h2>
            <p className="text-muted-foreground text-xs">
              {period.rangeLabel}
              {data?.timezone !== undefined && ` · ${data.timezone}`}
            </p>
          </div>
        </div>
        <span className="bg-muted text-muted-foreground rounded-full px-2.5 py-1 text-[11px] font-medium">{period.pill}</span>
      </div>

      {error !== null ? (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              icon={<BarChart3 className="size-4" />}
              label="Account platform ROAS"
              value={loading || bm === null ? null : roas(bm.account_platform_roas)}
              sub={loading || bm === null ? null : `Cutoff (< ${RATIO.toFixed(2)}×): ${roas(bm.platform_threshold)}`}
            />
            <KpiCard
              icon={<TrendingUp className="size-4" />}
              label={`Account ${modelLabel} ROAS`}
              value={loading || bm === null ? null : roas(bm.account_model_roas)}
              sub={loading || bm === null ? null : `Target (≥ ${RATIO.toFixed(2)}×): ${roas(bm.model_threshold)}`}
            />
            <KpiCard
              icon={<ShieldCheck className="size-4" />}
              label="Qualified hidden winners"
              value={loading || data === null ? null : String(qualifiedRows.length)}
              sub={model === "lc" ? "Protected conversion closers" : "Protected from accidental budget cuts"}
            />
            <KpiCard
              icon={<Sparkles className="size-4" />}
              label="Protected ad spend"
              value={loading || data === null ? null : inr(protectedSpend)}
              sub={model === "lc" ? "Active bottom-of-funnel closing budget" : "Active top-of-funnel discovery budget"}
            />
          </div>

          <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
            {/*
              Plain, low-key caption directly on the table it describes (owner request,
              2026-09-17) — no "Note:" label, no colour emphasis. It used to float above
              the KPI cards as a bold alert-styled line, disconnected from the rows it
              actually explains; this reads as ordinary table context instead.
            */}
            <p className="text-muted-foreground border-b px-3 py-2 text-xs">
              Every {entity === "ad" ? "ad" : "campaign"} below is treated as{" "}
              {model === "lc" ? "a Conversion/Sales campaign" : "an Awareness campaign"} under {modelLabel}{" "}
              attribution, regardless of its objective in {platformManagerLabel}.
            </p>
            <div className="flex flex-wrap items-center gap-2 border-b p-3">
              <TabButton active={tab === "qualified"} onClick={() => setTab("qualified")}>
                <ShieldCheck className="size-3.5" />
                Qualified winners
                <Count value={loading ? null : qualifiedRows.length} />
              </TabButton>
              <TabButton active={tab === "all"} onClick={() => setTab("all")}>
                <BarChart3 className="size-3.5" />
                All active {entityWord}
                <Count value={loading ? null : rows.length} />
              </TabButton>
            </div>

            <div className="max-h-[min(60svh,40rem)] overflow-auto">
              <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
                <colgroup>
                  {colWidths.map((w, i) => (
                    <col key={i} style={{ width: `${w}px` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th className="row-head text-foreground sticky top-0 left-0 z-30 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold relative">
                      {entity === "ad" ? "Ad name" : "Campaign name"}
                      <TableResizeHandle onResize={(e) => startResize(0, e)} />
                    </th>
                    <ColHead onResize={(e) => startResize(1, e)}>Spend</ColHead>
                    <ColHead onResize={(e) => startResize(2, e)}>Platform ROAS</ColHead>
                    <ColHead onResize={(e) => startResize(3, e)}>DDA ROAS</ColHead>
                    <ColHead onResize={(e) => startResize(4, e)}>{modelLabel} ROAS</ColHead>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }, (_, i) => (
                      <tr key={i} className={i % 2 === 1 ? "row-odd" : "row-even"}>
                        <td className="border-b px-3 py-3">
                          <Skeleton className="h-4 w-52" />
                        </td>
                        {Array.from({ length: 4 }, (_, j) => (
                          <td key={j} className="border-b px-3 py-3 text-right">
                            <Skeleton className="ml-auto h-4 w-14" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-muted-foreground px-4 py-12 text-center text-sm">
                        {tab === "qualified"
                          ? `No ${entityWord} met the hidden-winner rule for ${period.title.toLowerCase()} — ${storeName}.`
                          : `No ${entityWord} with spend above ₹2,000 in ${period.title.toLowerCase()} for ${storeName}.`}
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((r, idx) => {
                      const belowPlatform = bm !== null && r.platform_roas < bm.platform_threshold;
                      const meetsModel = bm !== null && r.model_roas >= bm.model_threshold && r.model_revenue > 0;
                      return (
                        <tr key={r.entity_id} className={cn("transition-colors", idx % 2 === 1 ? "row-odd" : "row-even")}>
                          <td className={cn("sticky left-0 z-10 border-b border-r px-3 py-3 max-w-0", idx % 2 === 1 ? "row-odd" : "row-even")}>
                            <span className="text-foreground block truncate text-xs font-medium" title={r.entity_name}>
                              {r.entity_name}
                            </span>
                          </td>
                          <td className="text-foreground border-b px-3 py-3 text-right text-xs tabular-nums">{inr(r.spend)}</td>
                          <td className="border-b px-3 py-3 text-right text-xs tabular-nums">
                            <span className="text-foreground">{roas(r.platform_roas)}</span>
                            {belowPlatform && <Chip tone="amber">&lt; {RATIO.toFixed(2)}×</Chip>}
                          </td>
                          <td className="text-muted-foreground border-b px-3 py-3 text-right text-xs tabular-nums">
                            {roas(r.dda_roas)}
                          </td>
                          <td className="border-b px-3 py-3 text-right text-xs tabular-nums">
                            <span className="text-foreground">{roas(r.model_roas)}</span>
                            {meetsModel && <Chip tone="emerald">≥ {RATIO.toFixed(2)}×</Chip>}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {!loading && filtered.length > 0 && (
                  <tfoot>
                    <tr className="row-head text-foreground font-semibold">
                      <td className="row-head sticky bottom-0 left-0 z-30 border-t-2 px-3 py-3 text-xs">
                        Total ({filtered.length} {entityWord})
                      </td>
                      <td className="row-head sticky bottom-0 z-20 border-t-2 px-3 py-3 text-right text-xs tabular-nums">
                        {inr(totals.spend)}
                      </td>
                      <td className="row-head sticky bottom-0 z-20 border-t-2 px-3 py-3 text-right text-xs tabular-nums">
                        {roas(totals.platform_roas)}
                      </td>
                      <td className="row-head sticky bottom-0 z-20 border-t-2 px-3 py-3 text-right text-xs tabular-nums">
                        {roas(totals.dda_roas)}
                      </td>
                      <td className="row-head sticky bottom-0 z-20 border-t-2 px-3 py-3 text-right text-xs tabular-nums">
                        {roas(totals.model_roas)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {!loading && filtered.length > 0 && totalPages > 1 && (
              <div className="flex items-center justify-end gap-1.5 border-t p-3">
                <PagerButton disabled={page <= 1} onClick={() => setPage((v) => Math.max(1, v - 1))}>
                  Previous
                </PagerButton>
                <span className="text-muted-foreground px-2 text-xs font-medium">
                  {page} / {totalPages}
                </span>
                <PagerButton disabled={page >= totalPages} onClick={() => setPage((v) => Math.min(totalPages, v + 1))}>
                  Next
                </PagerButton>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ColHead({
  children,
  onResize,
}: {
  readonly children: React.ReactNode;
  readonly onResize?: (event: React.MouseEvent) => void;
}): React.JSX.Element {
  return (
    <th className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-right text-[13px] font-semibold relative">
      <span className="pr-1">{children}</span>
      {onResize && <TableResizeHandle onResize={onResize} />}
    </th>
  );
}

function Chip({ tone, children }: { readonly tone: "amber" | "emerald"; readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <span
      className={cn(
        "ml-1.5 rounded px-1 py-0.5 text-[10px] font-bold",
        tone === "amber"
          ? "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
          : "bg-emerald-200 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200",
      )}
    >
      {children}
    </span>
  );
}

function Count({ value }: { readonly value: number | null }): React.JSX.Element {
  return (
    <span className="bg-muted text-muted-foreground ml-1 rounded px-1.5 py-0.5 text-[11px] font-semibold">
      {value === null ? "—" : value}
    </span>
  );
}

function KpiCard({
  icon,
  label,
  value,
  sub,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string | null;
  readonly sub: string | null;
}): React.JSX.Element {
  return (
    <Card className="gap-2 p-4">
      <div className="text-muted-foreground flex items-center justify-between text-xs font-medium tracking-wide uppercase">
        <span>{label}</span>
        <span className="text-primary">{icon}</span>
      </div>
      {value === null ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <div className="text-foreground text-2xl font-bold tracking-tight tabular-nums">{value}</div>
      )}
      {sub !== null && <div className="text-muted-foreground text-[11px]">{sub}</div>}
    </Card>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
        active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

function PagerButton({
  disabled,
  onClick,
  children,
}: {
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="border-border text-foreground hover:bg-accent h-8 rounded-md border px-2.5 text-xs transition-colors disabled:pointer-events-none disabled:opacity-50"
    >
      {children}
    </button>
  );
}
